import React, { ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { addDays, endOfYear, format, isBefore } from 'date-fns';
import { Link } from 'react-router';
import styles from './styles/Billing.module.scss';
import bannerStyles from '../LandingPage/LandingPageBanners/styles/Banners.module.scss';

import { OmaLummeApp } from '../../omaLummeContext';

// Components
import DatePicker from '../Common/DatePicker';
import Switch from '../Common/Switch';
import BillGraph from '../Graphs/BillGraph';
import Liftup from '../Common/Liftup';
import ChangePaymentMethod from './ChangePaymentMethod';
import LatestInvoice from './LatestInvoice';
import LatestInvoiceBarCode from './LatestInvoiceBarCode';
import MyBillsTable from './MyBillsTable';
import LSMBillsTable from './LSMBillsTable';
import Loading from '../Common/Loading';
import Box from '../Common/Box';
import Button from '../Common/Button';
import Dialog from '../Common/Dialog';
import Select from '../Common/Select';
import Snackbar from '../Common/Snackbar';

// Types
import { InvoiceLSM, Ledger } from '../../types/billing';
import { InvoiceDetailsObject, PostponeObject } from '../../types/consumptionRates';
// Actions
import { fetchInvoices, postponeInvoice, getCustomerNumberLSM, getInvoicesLSM } from '../../actions';
import { fetchPdf } from '../../actions';
// Utils
import { setPriceFormat, roundNumber, getCorrectBankAccount, formatDate } from '../../utils/billing';
import { gaEventIfAllowed, gaPageviewIfAllowed } from '../../utils/googleAnalytics';
import { generateMeteringPointOptions } from '../../utils/consumption';
import { disableManageContracts } from '../Contracts/ManageContracts/ManageContracts';

const OomiLockDownDate = new Date('2026-05-01T00:00:00+03:00');
const OomiLockDownDateProd = new Date('2026-09-01T00:00:00+03:00');
const currentDate = new Date();
export const disableEInvoiceOrder = currentDate >= OomiLockDownDateProd;

const Billing: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const dispatch = OmaLummeApp.useDispatch();
  const { basicData, loadingBasicData, users, invoices, invoicesNextPageToken, ledgers, selectedMeteringPoint } = OmaLummeApp.useState();

  const [desiredPdf, setDesiredPdf] = React.useState<number>();
  const [selectedLedger, setSelectedLedger] = React.useState<number>(0);
  const [activeLedgerDetails, setActiveLedgerDetails] = React.useState<Ledger | undefined>(undefined);
  const { userId } = users.activeUser;
  const [pageview, setPageview] = React.useState<boolean>(false);
  const [postponing, setPostponing] = React.useState<boolean>(false);
  const [postponeVisibility, setPostponeVisibility] = React.useState<string>('');
  const [postponed, setPostponed] = React.useState<string>('');
  const [postponeError, setPostponeError] = React.useState<string>('');
  const [mostRecentInvoiceDate, setMostRecentInvoiceDate] = React.useState<string>('');
  const [mostRecentInvoiceNumber, setMostRecentInvoiceNumber] = React.useState<string>('');
  const [showInvoicesAsGraph, setShowInvoicesAsGraph] = React.useState<boolean>(false);
  const [bankAccountNumber, setBankAccountNumber] = React.useState<string>();
  const [userHasLSMUserId, setUserHasLSMUserId] = React.useState<boolean>(false);
  const [fetchingLSMInvoices, setFetchingLSMInvoices] = React.useState<boolean>(false);
  const [LSMInvoices, setLSMInvoices] = React.useState<InvoiceLSM[] | undefined>(undefined);
  const [changingPaymentMethod, setChangingPaymentMethod] = React.useState<boolean>(false);
  const [paymentMethodUpdated, setPaymentMethodUpdated] = React.useState<boolean>(false);

  const [startDate, setStartDate] = React.useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = React.useState<Date | undefined>(undefined);

  const [showOomiBanner, setShowOomiBanner] = React.useState<boolean>(
    new Date() > new Date('2026-01-01T00:00:00+03:00') && localStorage.getItem('OomiBillingBannerDisplayed') !== 'true',
  );

  const [activeOption, setActiveOption] = React.useState<string>('all');

  const meteringPointOptions = generateMeteringPointOptions(basicData ?? [], true, ledgers);

  if (meteringPointOptions.length === 1 && activeOption === 'all' && meteringPointOptions[0].value !== 'all') {
    setActiveOption(meteringPointOptions[0].value);
  }

  const contract =
    selectedMeteringPoint &&
    basicData?.find((d) => d.GSRN === selectedMeteringPoint.meteringPointId && d.contractNo === selectedMeteringPoint.contractNo.toString());

  const filteredInvoices = invoices?.filter((invoice) => {
    if (activeOption !== 'all') {
      const optionArr = activeOption.split('-');
      return invoice.contractIdentifier === optionArr[2];
    } else if (selectedLedger > 0) {
      return invoice.ledgerNo === selectedLedger.toString();
    }
    return true;
  });

  React.useEffect(() => {
    if (!pageview) {
      gaPageviewIfAllowed('/billing');
      setPageview(true);
    }
  }, [pageview]);

  React.useEffect(() => {
    let unMounted = false;

    if (!unMounted) {
      setActiveOption('all');
      setActiveLedgerDetails(undefined);
      setFetchingLSMInvoices(false);
      setLSMInvoices(undefined);
    }

    return (): void => {
      unMounted = true;
    };
  }, [userId]);

  React.useEffect(() => {
    if (activeOption !== 'all') {
      if (activeOption.split('-')[0] !== 'null') {
        setSelectedLedger(0);
      }
    }
  }, [activeOption]);

  React.useEffect(() => {
    async function fetchDesiredPdf(): Promise<void> {
      if (desiredPdf) {
        const dataOwnerCustomerId: number =
          selectedMeteringPoint?.customerId && selectedMeteringPoint?.customerId !== 0 ? selectedMeteringPoint?.customerId : userId;
        const contractNo = selectedMeteringPoint?.contractNo;

        const invoiceIsLSM = LSMInvoices?.find((invoice) => invoice.LSM_invoiceNo === desiredPdf.toString());

        if (dataOwnerCustomerId) {
          const fileBlob = await fetchPdf(dataOwnerCustomerId, userId, contractNo ?? 0, desiredPdf, invoiceIsLSM ? 'lsm' : 'lumme');

          const file = new Blob([fileBlob], { type: 'application/pdf' });
          const fileUrl = URL.createObjectURL(file);

          const invisiblePdfLink = document.createElement('a');
          document.body.appendChild(invisiblePdfLink);
          invisiblePdfLink.href = fileUrl;
          invisiblePdfLink.download = `${desiredPdf}.pdf`;
          invisiblePdfLink.click();
          window.URL.revokeObjectURL(fileUrl);
        }

        setDesiredPdf(undefined);
      }
    }

    fetchDesiredPdf();
  }, [userId, desiredPdf, selectedMeteringPoint, users, LSMInvoices]);

  React.useEffect(() => {
    let unMounted = false;

    async function checkLSMUserID(): Promise<void> {
      const LSMUser = await getCustomerNumberLSM(userId);

      if (LSMUser && LSMUser.length > 0 && LSMUser[0].propertyName.toLowerCase() === 'loiste asiakasnumero') {
        if (!unMounted) {
          setUserHasLSMUserId(true);
        }
      } else {
        if (!unMounted) {
          setUserHasLSMUserId(false);
        }
      }
    }

    if (userId !== 0 && !unMounted) {
      checkLSMUserID();
    }

    return (): void => {
      unMounted = true;
    };
  }, [userId]);

  const handleInputChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    event.persist();

    const value = event.target.value;

    setSelectedLedger(value === 'all' ? 0 : parseInt(value));
  };

  let invoicesTable,
    barCode = <Loading />;
  let latestInvoice = <Loading />;
  let invoiceTitle = <p></p>;
  let bankAccountFound = false;
  let pdfButton;

  const fetchMoreInvoices = (test = false, page = invoicesNextPageToken): void => {
    async function loadMoreInvoices(test: boolean): Promise<void> {
      if (page !== null) {
        const moreInvoicesQuery = await fetchInvoices('customer', userId, userId, 'all', 'all', page);
        const moreInvoices = moreInvoicesQuery.invoices.filter((invoice) => !invoices?.some((inv) => inv.identifier === invoice.identifier));

        if (!test) {
          dispatch(['changeInvoices', invoices?.concat(moreInvoices)]);
          dispatch(['changeInvoicesNextPageToken', moreInvoicesQuery.nextPageToken]);
        }
      }
    }
    loadMoreInvoices(test);
  };

  React.useEffect(() => {
    if (invoices && invoices[0]) {
      invoices.sort((a, b) => (new Date(a.dateOfInvoice) < new Date(b.dateOfInvoice) ? 1 : -1));

      setMostRecentInvoiceDate(invoices[0].dueDate);
      setMostRecentInvoiceNumber(invoices[0].identifier);
    }
  }, [invoices]);


  const postponeThis = (e: React.MouseEvent<Element>, dataOwnerCustomerId: number, invoiceNo: string): void => {
    e.preventDefault();

    async function postpone(): Promise<void> {
      const postponeRequest = await postponeInvoice(dataOwnerCustomerId, userId, invoiceNo);

      if (postponeRequest.message === 'Invoice postponed.') {
        setPostponeVisibility('');
        setPostponed(`${dataOwnerCustomerId}-${invoiceNo}`);
        setPostponing(false);
      } else {
        setPostponing(false);
        setPostponeError(`${t('consumption:consumption:postponeFailed')}`);
      }
    }
    setPostponeError('');
    setPostponing(true);
    postpone();
  };

  const postpone: PostponeObject = {
    postponed: postponed,
    postponeVisibility: postponeVisibility,
    setPostponeVisibility: setPostponeVisibility,
  };

  React.useEffect(() => {
    let now = format(new Date(), 'yyyy-MM-dd');

    if (invoices && invoices.length > 0) {
      invoices.sort((a, b) => (a.dueDate < b.dueDate ? 1 : -1));
      if (invoices[0]) {
        now = invoices[0].dueDate;
      }
      if (!startDate || isBefore(new Date(invoices[invoices.length - 1].dueDate), startDate)) {
        setStartDate(new Date(invoices[invoices.length - 1].dueDate));
      }
    }

    setEndDate(new Date(now));
  }, [invoices, startDate]);

  if (filteredInvoices) {
    invoicesTable = (
      <MyBillsTable
        invoices={filteredInvoices}
        desiredPdf={desiredPdf}
        setDesiredPdf={setDesiredPdf}
        postpone={postpone}
        mostRecentInvoiceDate={mostRecentInvoiceDate}
        mostRecentInvoiceNumber={mostRecentInvoiceNumber}
        startDate={startDate}
        endDate={endDate}
      />
    );

    if (filteredInvoices.length > 0) {
      const mostRecentInvoice = filteredInvoices.sort((a, b) => (new Date(a.dateOfInvoice) < new Date(b.dateOfInvoice) ? 1 : -1))[0];

      const invoiceDetails: InvoiceDetailsObject = {
        customerId: mostRecentInvoice.customerIdentifier,
        meteringPointAddress: basicData?.find((d) => d.contractCode === mostRecentInvoice.contractIdentifier)?.meteringpointAddress,
        amount: mostRecentInvoice.totalWithVat,
        invoiceState: mostRecentInvoice.status,
        invoiceDue: mostRecentInvoiceDate,
        invoiceNumber: mostRecentInvoiceNumber,
        invoiceRefNo: mostRecentInvoice.referenceNumber,
        postponable: mostRecentInvoice.dueDatePostponeable,
        company: mostRecentInvoice.company,
      };
      if (mostRecentInvoiceNumber === '') {
        setMostRecentInvoiceNumber(mostRecentInvoice.identifier);
      }
      if (mostRecentInvoiceDate === '') {
        setMostRecentInvoiceDate(mostRecentInvoice.dueDate);
      }

      pdfButton = (
        <>
          <Button className="buttonWhiteSmall" onClick={(): void => setDesiredPdf(parseInt(mostRecentInvoice.identifier))}>
            {t('billing:billing.openPdf')}
          </Button>
          {desiredPdf && desiredPdf === parseInt(mostRecentInvoice.identifier) && <Loading size="small" />}
        </>
      );

      const formattedSum = `${setPriceFormat(roundNumber(mostRecentInvoice.totalWithVat))} €`;
      invoiceTitle = <div className="h1">{formattedSum}</div>;

      const mostRecentInvoiceLedger = ledgers?.find((ledger) => ledger.ledgerNo.toString() === mostRecentInvoice.ledgerNo);

      if (activeLedgerDetails?.ledgerNo !== mostRecentInvoiceLedger?.ledgerNo) {
        setActiveLedgerDetails(mostRecentInvoiceLedger);
      }

      if (ledgers && activeLedgerDetails) {
        // Find the correct bank account based on contract number.
        const bankAccount = getCorrectBankAccount(invoiceDetails.company);
        if (bankAccount && bankAccountNumber !== bankAccount) {
          setBankAccountNumber(bankAccount);
        }
        if (bankAccount && invoiceDetails.amount >= 0) {
          activeLedgerDetails.BankAccount = bankAccount;
          bankAccountFound = true;
          barCode = <LatestInvoiceBarCode invoiceDetails={invoiceDetails} ledgerDetails={activeLedgerDetails} />;
        }

        if (activeLedgerDetails?.paymentType) {
          invoiceDetails.paymentType = activeLedgerDetails.paymentType.paymentTypeNo;
        }
      }
      latestInvoice = (
        <>
          <LatestInvoice
            invoiceDetails={invoiceDetails}
            bankAccount={bankAccountNumber}
            postpone={postpone}
            setChangingPaymentMethod={setChangingPaymentMethod}
            paymentMethodUpdated={paymentMethodUpdated}
          />
          {bankAccountFound && barCode}
          {pdfButton}
        </>
      );
    } else {
      latestInvoice = <p>{t('billing:billing.noInvoices')}</p>;
    }
  }

  const fetchLSMInvoices = (): void => {
    async function loadLSMInvoices(): Promise<void> {
      setFetchingLSMInvoices(true);
      const LSMInvoices = await getInvoicesLSM(userId);

      if (LSMInvoices && LSMInvoices.length > 0) {
        setLSMInvoices(LSMInvoices.filter((invoice) => invoice.LSM_invoiceNo && invoice.invoiceSum).sort((a, b) => (a.dueDate < b.dueDate ? 1 : -1)));
      }
      setFetchingLSMInvoices(false);
    }
    if (userHasLSMUserId) {
      loadLSMInvoices();
    }
  };

  return (
    <>
      {changingPaymentMethod && contract && activeLedgerDetails && activeLedgerDetails.paymentType && (
        <ChangePaymentMethod
          contract={contract}
          currentPaymentType={activeLedgerDetails.paymentType.paymentTypeNo}
          setChangingPaymentMethod={setChangingPaymentMethod}
          setPaymentMethodUpdated={setPaymentMethodUpdated}
        />
      )}
      {!changingPaymentMethod && (
        <>
          {showOomiBanner && (
            <Box className={bannerStyles.oomiBanner}>
              <button
                className={bannerStyles.dismiss}
                onClick={() => {
                  setShowOomiBanner(false);
                  window.localStorage.setItem('OomiBillingBannerDisplayed', 'true');
                }}
              >
                <span>{t(`common:general.close`)}</span>
              </button>
              <h4>{t(`billing:oomiBanner.title`)}</h4>

              <p>
                {t(`billing:oomiBanner.description`)}{' '}
                <a href="https://www.lumme-energia.fi/oomi" target="_blank" rel="noopener noreferrer">
                  {t(`billing:oomiBanner.link`)}
                </a>
              </p>
            </Box>
          )}
          <Box wrapper size="double">
            <Box size={filteredInvoices && filteredInvoices.length > 0 ? 'half' : 'full'}>
              <h1 aria-live="polite">{t('billing:billing.billingTitle')}</h1>
              <p> {t('billing:billing.billingIngress')} </p>
              <p>
                <Trans i18nKey="billing:billing.billingIngress2" values={{ consumptionLinkText: t(`billing:billing.consumptionLinkText`) }}>
                  . <Link to="/consumption"></Link>
                </Trans>
              </p>

              {!loadingBasicData && (!basicData || (basicData && basicData.length === 0)) && (
                <p>
                  <strong>{t(`common:general.contractsOrMeteringPointsNotFound`)}</strong>
                </p>
              )}

              {!loadingBasicData && basicData && basicData.length > 0 && (
                <>
                  <h2>{t('consumption:consumption.place')}</h2>

                  <div className={styles.select}>
                    <form>
                      <Select
                        options={meteringPointOptions}
                        contractGrouping={meteringPointOptions.length > 1}
                        activeOption={activeOption}
                        setActiveOption={setActiveOption}
                        idToggle
                        size="big"
                        search={meteringPointOptions.length > 5 ? t(`consumption:consumption.searchMeteringPoint`) : undefined}
                      />
                    </form>
                  </div>
                  {ledgers && ledgers.length > 1 && (
                    <>
                      <h2>{t('billing:billing.ledgers')}</h2>
                      <div className="select">
                        <select id="ledger" onChange={handleInputChange} value={selectedLedger}>
                          <option value="all">{t('billing:billing.select')}</option>
                          {ledgers.map((ledger) => (
                            <option value={ledger.ledgerNo} key={ledger.ledgerNo}>
                              {ledger.ledgerNo}
                              {ledger.ledgerName && ` – ${ledger.ledgerName}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </>
              )}
            </Box>
            {filteredInvoices && filteredInvoices.length > 0 && (
              <Box size="half" color="lavender">
                <header className={styles.header}>
                  <div className={styles.icon}></div>
                  <div>
                    <h4>{t('billing:billing.billingFlipcardTitle')}</h4>
                    {invoiceTitle}
                  </div>
                </header>
                {latestInvoice}
              </Box>
            )}
          </Box>

          {!disableEInvoiceOrder && !paymentMethodUpdated && activeLedgerDetails?.paymentType?.paymentTypeNo === 1 && users.activeUser.customerType !== 'business' && (
            <Box noStyles>
              <Liftup>
                <div className={styles.eBillPromo}>
                  <h2>{t(`billing:eBillPromo.title`)}</h2>
                  <p>{t(`billing:eBillPromo.description`)}</p>
                  <Button onClick={() => setChangingPaymentMethod(true)}>{t(`billing:eBillPromo.cta`)}</Button>
                </div>
              </Liftup>
            </Box>
          )}

          <Dialog rounded openModal={postponeVisibility !== ''} closeModal={() => setPostponeVisibility('')}>
            <div className={styles.postponeDialog}>
              <h2>{t(`consumption:consumption.postponeDialogTitle`)}</h2>
              <p>
                <Trans
                  i18nKey="consumption:consumption.postponeDialogBody"
                  values={{ postponeCost: t(`consumption:consumption.postponeDialogBodyCost`) }}
                >
                  . <strong></strong>
                </Trans>
              </p>

              <p>{t(`consumption:consumption.postponeDialogNewDueDate`)}</p>
              <p className={styles.postponeDialogNewDate}>
                {formatDate(
                  addDays(
                    new Date(filteredInvoices?.find((i) => i.identifier === postponeVisibility.split('-')[1])?.dueDate || new Date()),
                    14,
                  ).toISOString(),
                )}
              </p>

              {postponeError !== '' && <p>{postponeError}</p>}

              {postponing && <Loading size="small" />}
              {!postponing && (
                <>
                  <Button
                    className="buttonWhite"
                    onClick={(e): void => {
                      e.preventDefault();
                      setPostponeVisibility('');
                      setPostponeError('');
                    }}
                  >
                    {t('common:general.cancel')}
                  </Button>
                  <Button
                    onClick={(e): void => {
                      const customerId = parseInt(postponeVisibility.split('-')[0]);
                      const invoiceNumber = postponeVisibility.split('-')[1];

                      postponeThis(e, customerId, invoiceNumber);
                      gaEventIfAllowed({
                        category: 'Eräpäivän siirto',
                        action: 'Tallenna eräpäivän siirto',
                      });
                    }}
                  >
                    {t('common:general.confirm')}
                  </Button>
                </>
              )}
            </div>
          </Dialog>

          {postponeVisibility === '' && postponed !== '' && (
            <Snackbar type="success" fade>
              {t(`consumption:consumption.postponeSnackbarSuccess`)}
            </Snackbar>
          )}

          {paymentMethodUpdated && (
            <Snackbar type="success" fade>
              {t(`billing:changePayment.success`)}
            </Snackbar>
          )}

          {!loadingBasicData && basicData && basicData.length > 0 && (
            <Box>
              {!filteredInvoices && <Loading />}
              {filteredInvoices && filteredInvoices.length === 0 && <p>{t('billing:billing.noInvoices')}</p>}
              {filteredInvoices && filteredInvoices.length > 0 && (
                <>
                  <h2>{t('billing:billing.billingTableTitle')}</h2>

                  <div className={styles.invoicesHeader}>
                    <div>
                      <DatePicker
                        type="range"
                        dates={{ startDate: startDate, setStartDate: setStartDate, endDate: endDate, setEndDate: setEndDate }}
                        limits={{ minDate: undefined, maxDate: endOfYear(new Date()) }}
                      />
                    </div>
                    <Switch
                      offLabel={t(`billing:billing.switchTable`)}
                      onLabel={t(`billing:billing.switchGraph`)}
                      active={showInvoicesAsGraph}
                      onChange={() => setShowInvoicesAsGraph(!showInvoicesAsGraph)}
                    />
                  </div>

                  {showInvoicesAsGraph && filteredInvoices && (
                    <div className={styles.history}>
                      <BillGraph invoices={filteredInvoices} startDate={startDate} endDate={endDate} />
                      <Button className="buttonWhite" link="/consumption">
                        {t('billing:billing.consumptionLink')}
                      </Button>
                    </div>
                  )}
                  {!showInvoicesAsGraph && invoicesTable}
                  {invoicesNextPageToken !== null && (
                    <Button onClick={(): void => fetchMoreInvoices()}>{t('billing:billing.billingTableLoadMore')}</Button>
                  )}
                </>
              )}
              {userHasLSMUserId && !fetchingLSMInvoices && (!LSMInvoices || LSMInvoices.length < 1) && (
                <Button onClick={(): void => fetchLSMInvoices()}>{t('billing:billing.billingTableLoadLSM')}</Button>
              )}
              {fetchingLSMInvoices && <Loading size="small" />}
              {LSMInvoices && LSMInvoices.length > 0 && (
                <LSMBillsTable
                  invoices={LSMInvoices}
                  desiredPdf={desiredPdf}
                  setDesiredPdf={setDesiredPdf}
                  postpone={postpone}
                  mostRecentInvoiceDate={mostRecentInvoiceDate}
                  mostRecentInvoiceNumber={mostRecentInvoiceNumber}
                />
              )}
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default Billing;
